// Pagination — page-N controls for the result column.
//
// Platform-level (CLAUDE.md Iron Rule 11). Every entity browser that wants
// page-N navigation renders this through EntityScreen's `pagination` slot;
// no entity surface owns its own pager.
//
// State model: 1-indexed `page` + `pageSize`. The host owns the state and
// persists it to the URL (`?page=N`) so back-button navigation lands on
// the same page of the same sort/filter combo. Filter/sort changes reset
// page → 1 at the host (this component is controlled).
//
// `total` is optional. When null (the search backend short-circuits COUNT(*))
// we render "Page N · 50/page" and rely on `hasMore` to enable Next.

import type { CSSProperties } from 'react';

export interface PaginationProps {
  page: number; // 1-indexed
  pageSize: number;
  total?: number | null;
  hasMore?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Allowed page sizes for the picker. Default [25, 50, 100, 200]. */
  pageSizes?: number[];
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 200];

export function Pagination({
  page,
  pageSize,
  total,
  hasMore,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
}: PaginationProps) {
  const totalPages =
    typeof total === 'number' && total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : null;

  const canPrev = page > 1;
  const canNext = totalPages != null ? page < totalPages : Boolean(hasMore);

  return (
    <div data-component="Pagination" style={containerStyle}>
      <button
        type="button"
        className="btn xs gh"
        disabled={!canPrev}
        onClick={() => canPrev && onPageChange(page - 1)}
        aria-label="previous page"
      >
        ‹ PREV
      </button>

      <span style={pageLabelStyle}>
        {totalPages != null ? (
          <>
            PAGE <b style={emphStyle}>{page}</b> / {totalPages.toLocaleString()}
          </>
        ) : (
          <>
            PAGE <b style={emphStyle}>{page}</b>
          </>
        )}
      </span>

      <button
        type="button"
        className="btn xs gh"
        disabled={!canNext}
        onClick={() => canNext && onPageChange(page + 1)}
        aria-label="next page"
      >
        NEXT ›
      </button>

      {onPageSizeChange && (
        <label style={pageSizeStyle}>
          <span style={{ opacity: 0.7 }}>ROWS</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10);
              if (Number.isFinite(next) && next > 0) onPageSizeChange(next);
            }}
            style={selectStyle}
            aria-label="rows per page"
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontFamily: 'var(--mono)',
  // Brighter, larger than the meta bar so the pager is the obvious
  // affordance — sits in its own centered footer row inside EntityScreen.
  fontSize: 12,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--fg-1)',
  fontVariantNumeric: 'tabular-nums',
};

const pageLabelStyle: CSSProperties = {
  padding: '0 8px',
  minWidth: 100,
  textAlign: 'center',
};

const emphStyle: CSSProperties = {
  color: 'var(--accent, var(--fg-1))',
  fontWeight: 700,
  fontSize: 14,
};

const pageSizeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginLeft: 12,
  paddingLeft: 12,
  borderLeft: '1px solid var(--bd)',
};

const selectStyle: CSSProperties = {
  background: 'var(--bg-pane)',
  color: 'var(--fg-1)',
  border: '1px solid var(--bd)',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  padding: '2px 4px',
};
